import { ENDPOINTS, getFullUrl } from './apiConfig';

/**
 * Helper function to get auth token from localStorage
 */
const getAuthToken = () => {
  return localStorage.getItem('accessToken');
};

/**
 * Helper function to create headers with authentication
 */
const createHeaders = (includeAuth = true) => {
  const headers = {
    'Content-Type': 'application/json',
  };
  
  if (includeAuth) {
    const token = getAuthToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  }
  
  return headers;
};

/**
 * Helper function to handle API responses
 */
const handleResponse = async (response) => {
  if (!response.ok) {
    if (response.status === 401) {
      console.error('API Error: Unauthorized. Token might be invalid or expired.');
      throw new Error(`API Error: ${response.status} Unauthorized - Please log in again.`);
    }
    
    const errorDetails = await response.json().catch(() => ({})); 
    console.error('API Error Response:', response.status, response.statusText, errorDetails);
    throw new Error(`API Error: ${response.status} ${response.statusText} - ${errorDetails.detail || errorDetails.message || 'Request failed'}`);
  }
  
  return response.json();
};

/**
 * Service object for attendance-related API calls.
 */
const attendanceService = {
  /**
   * Fetches attendance records for a specific date.
   */
  getAttendanceByDate: async (date) => {
    const url = getFullUrl(ENDPOINTS.GET_ATTENDANCE_BY_DATE);
    
    console.log('Sending POST request to:', url);
    console.log('Payload:', JSON.stringify({ date }));

    const token = getAuthToken();
    if (!token) {
      console.warn('No access token found for API request.');
      throw new Error('Authentication required.');
    }

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: createHeaders(),
        body: JSON.stringify({ date }),
      });

      let result = await handleResponse(response);
      console.log('API Success Response (Attendance):', result);

      // Recompute status on frontend: flatten user+attendance into shape expected by the table
      if (Array.isArray(result)) {
        result = result.map(item => {
          const attendance = item.attendance || {};
          let computedStatus = item.status;
          if (attendance && Object.keys(attendance).length) {
            const inTime = attendance.in_time;
            if (inTime && typeof inTime === 'string' && inTime > '08:45:00') {
              computedStatus = 'late';
            } else {
              computedStatus = 'present';
            }
          }

          // Flatten into the shape the table expects: top-level user fields + attendance fields
          return {
            ...item.user,
            ...attendance,
            status: computedStatus
          };
        });
      }

      return result;

    } catch (error) {
      console.error('Network or Fetch Error fetching attendance:', error);
      if (error.message.startsWith('API Error:')) {
        throw error; 
      } else {
        throw new Error(`Network error: ${error.message}`);
      }
    }
  },

  updateAttendance: async (updateData) => {
    const url = getFullUrl(ENDPOINTS.UPDATE_ATTENDANCE);
    
    console.log('Sending PUT request to:', url); // Changed from POST
    console.log('Payload:', JSON.stringify(updateData));

    const token = getAuthToken();
    if (!token) {
      console.warn('No access token found for API request.');
      throw new Error('Authentication required.');
    }

    try {
      const response = await fetch(url, {
        method: 'PUT', // Changed from 'POST' to 'PUT'
        headers: createHeaders(),
        body: JSON.stringify(updateData),
      });

      const result = await handleResponse(response);
      console.log('API Success Response (Update Attendance):', result);
      return result;

    } catch (error) {
      console.error('Network or Fetch Error updating attendance:', error);
      if (error.message.startsWith('API Error:')) {
        throw error; 
      } else {
        throw new Error(`Network error: ${error.message}`);
      }
    }
  },

  /**
   * Edit attendance in_time/out_time and recompute duration
   */
  editAttendanceTimes: async (payload) => {
    const url = getFullUrl(ENDPOINTS.EDIT_TIMES);
    console.log('Sending PUT request to:', url);
    console.log('Payload:', JSON.stringify(payload));

    const token = getAuthToken();
    if (!token) {
      console.warn('No access token found for API request.');
      throw new Error('Authentication required.');
    }

    try {
      const response = await fetch(url, {
        method: 'PUT',
        headers: createHeaders(),
        body: JSON.stringify(payload),
      });

      const result = await handleResponse(response);
      console.log('API Success Response (Edit Attendance Times):', result);
      return result;
    } catch (error) {
      console.error('Network or Fetch Error editing attendance times:', error);
      if (error.message.startsWith('API Error:')) throw error;
      throw new Error(`Network error: ${error.message}`);
    }
  },

  /**
   * Checks in a user for attendance.
   */
  checkIn: async (attendanceData) => {
    const url = getFullUrl(ENDPOINTS.CHECKIN);
    
    // Determine the location value based on location type
    let locationValue;
    if (attendanceData.location_type === 'Site') {
      locationValue = attendanceData.site; // Use site name for Site type
    } else if (attendanceData.location_type === 'Meeting') {
      locationValue = attendanceData.meeting_location; // Use meeting location for Meeting type
    } else if (attendanceData.location_type === 'WFH') {
      locationValue = 'Work From Home'; // Use standard text for WFH
    } else {
      locationValue = attendanceData.address; // Fallback to address
    }
    
    // Create the proper body format expected by Django API
    const requestBody = {
      date: attendanceData.date,
      in_location_type: attendanceData.location_type,
      in_latitude: parseFloat(attendanceData.latitude),
      in_longitude: parseFloat(attendanceData.longitude),
      in_location: locationValue, // Use the determined location value
      site: attendanceData.site || null,
      meeting_location: attendanceData.meeting_location || null,
      in_time: attendanceData.in_time || new Date().toTimeString().split(' ')[0]
    };
    
    console.log('Sending POST request to:', url);
    console.log('Check-in Payload:', JSON.stringify(requestBody, null, 2));

    const token = getAuthToken();
    if (!token) {
      console.warn('No access token found for API request.');
      throw new Error('Authentication required.');
    }

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: createHeaders(),
        body: JSON.stringify(requestBody),
      });

      const result = await handleResponse(response);
      console.log('API Success Response (Check In):', result);
      return result;

    } catch (error) {
      console.error('Network or Fetch Error during check-in:', error);
      if (error.message.startsWith('API Error:')) {
        throw error; 
      } else {
        throw new Error(`Network error: ${error.message}`);
      }
    }
  },

  /**
   * Checks out a user from attendance.
   */
  checkOut: async (attendanceData) => {
    const url = getFullUrl(ENDPOINTS.CHECKOUT);
    
    // Determine the location value based on location type
    let locationValue;
    if (attendanceData.location_type === 'Site') {
      locationValue = attendanceData.site; // Use site name for Site type
    } else if (attendanceData.location_type === 'Meeting') {
      locationValue = attendanceData.meeting_location; // Use meeting location for Meeting type
    } else if (attendanceData.location_type === 'WFH') {
      locationValue = 'Work From Home'; // Use standard text for WFH
    } else {
      locationValue = attendanceData.address; // Fallback to address
    }
    
    // Create the proper body format expected by Django API
    const requestBody = {
      date: attendanceData.date,
      out_location_type: attendanceData.location_type,
      out_latitude: parseFloat(attendanceData.latitude),
      out_longitude: parseFloat(attendanceData.longitude),
      out_location: locationValue, // Use the determined location value
      site: attendanceData.site || null,
      meeting_location: attendanceData.meeting_location || null,
      out_time: attendanceData.out_time || new Date().toTimeString().split(' ')[0]
    };
    
    console.log('Sending POST request to:', url);
    console.log('Check-out Payload:', JSON.stringify(requestBody, null, 2));

    const token = getAuthToken();
    if (!token) {
      console.warn('No access token found for API request.');
      throw new Error('Authentication required.');
    }

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: createHeaders(),
        body: JSON.stringify(requestBody),
      });

      const result = await handleResponse(response);
      console.log('API Success Response (Check Out):', result);
      return result;

    } catch (error) {
      console.error('Network or Fetch Error during check-out:', error);
      if (error.message.startsWith('API Error:')) {
        throw error; 
      } else {
        throw new Error(`Network error: ${error.message}`);
      }
    }
  },

  /**
   * Fetches today's attendance records for the logged-in user.
   */
  getTodayAttendance: async () => {
    const url = getFullUrl(ENDPOINTS.GET_TODAY_ATTENDANCE);
    
    console.log('Sending GET request to:', url);

    const token = getAuthToken();
    if (!token) {
      console.warn('No access token found for API request.');
      throw new Error('Authentication required.');
    }

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: createHeaders(),
      });

      const result = await handleResponse(response);
      console.log('API Success Response (Today\'s Attendance):', result);
      return result;

    } catch (error) {
      console.error('Network or Fetch Error fetching today\'s attendance:', error);
      if (error.message.startsWith('API Error:')) {
        throw error; 
      } else {
        throw new Error(`Network error: ${error.message}`);
      }
    }
  },

  /**
   * Fetch attendance report for a date range (company-wide)
   */
  getAttendanceReport: async (start_date, end_date) => {
    const url = getFullUrl(ENDPOINTS.GET_ATTENDANCE_REPORT);
    console.log('Sending POST request to:', url);
    console.log('Payload:', JSON.stringify({ start_date, end_date }));

    const token = getAuthToken();
    if (!token) {
      console.warn('No access token found for API request.');
      throw new Error('Authentication required.');
    }

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: createHeaders(),
        body: JSON.stringify({ start_date, end_date }),
      });

      const result = await handleResponse(response);
      console.log('API Success Response (Attendance Report):', result);
      return result;
    } catch (error) {
      console.error('Network or Fetch Error fetching attendance report:', error);
      if (error.message.startsWith('API Error:')) throw error;
      throw new Error(`Network error: ${error.message}`);
    }
  },

  /**
   * Fetch attendance analytics for a date range (company-wide)
   */
  getAttendanceAnalytics: async (start_date, end_date) => {
    const url = getFullUrl(ENDPOINTS.GET_ATTENDANCE_ANALYTICS);
    console.log('Sending POST request to:', url);
    console.log('Payload:', JSON.stringify({ start_date, end_date }));

    const token = getAuthToken();
    if (!token) {
      console.warn('No access token found for API request.');
      throw new Error('Authentication required.');
    }

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: createHeaders(),
        body: JSON.stringify({ start_date, end_date }),
      });

      const result = await handleResponse(response);
      console.log('API Success Response (Attendance Analytics):', result);
      return result;
    } catch (error) {
      console.error('Network or Fetch Error fetching attendance analytics:', error);
      if (error.message.startsWith('API Error:')) throw error;
      throw new Error(`Network error: ${error.message}`);
    }
  },

  /**
   * List attendance corrections for the authenticated company
   */
  getAttendanceCorrections: async () => {
    const url = getFullUrl(ENDPOINTS.GET_ATTENDANCE_CORRECTIONS);
    console.log('Sending GET request to:', url);
    const token = getAuthToken();
    if (!token) throw new Error('Authentication required.');
    try {
      const response = await fetch(url, { method: 'GET', headers: createHeaders() });
      const result = await handleResponse(response);
      return Array.isArray(result) ? result : [];
    } catch (error) {
      console.error('Error fetching attendance corrections:', error);
      if (error.message.startsWith('API Error:')) throw error;
      throw new Error(`Network error: ${error.message}`);
    }
  },

  /**
   * Update an attendance correction (status/topic)
   */
  updateAttendanceCorrection: async (id, payload) => {
    const endpoint = ENDPOINTS.UPDATE_ATTENDANCE_CORRECTION.replace(':id', id);
    const url = getFullUrl(endpoint);
    console.log('Sending PUT request to:', url);
    console.log('Payload:', JSON.stringify(payload));
    const token = getAuthToken();
    if (!token) throw new Error('Authentication required.');
    try {
      const response = await fetch(url, { method: 'PUT', headers: createHeaders(), body: JSON.stringify(payload) });
      const result = await handleResponse(response);
      return result;
    } catch (error) {
      console.error('Error updating attendance correction:', error);
      if (error.message.startsWith('API Error:')) throw error;
      throw new Error(`Network error: ${error.message}`);
    }
  },

  /**
   * Fetches all sites or boundaries for the logged-in user.
   */
  getAllSites: async () => {
    const url = getFullUrl(ENDPOINTS.GET_ALL_SITES);
    
    console.log('Sending GET request to:', url);

    const token = getAuthToken();
    if (!token) {
      console.warn('No access token found for API request.');
      throw new Error('Authentication required.');
    }

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: createHeaders(),
      });

      const result = await handleResponse(response);
      console.log('API Success Response (Sites):', result);

      // Fix: use result.data if it exists, else fallback to result
      const sitesArray = Array.isArray(result.data) ? result.data : result;

      return sitesArray.map(site => ({
        id: site.id,
        name: site.name || site.site_name || site.location || `Site ${site.id}`,
        boundary: site.boundary || site.polygon || site.geofence || ''
      }));

    } catch (error) {
      console.error('Network or Fetch Error fetching sites:', error);
      if (error.message.startsWith('API Error:')) {
        throw error; 
      } else {
        throw new Error(`Network error: ${error.message}`);
      }
    }
  },

  /**
   * Get user attendance for today
   */
  getUserAttendanceForToday: async (username) => {
    try {
      // Try to get today's attendance first
      const todayAttendance = await attendanceService.getTodayAttendance();
      
      if (Array.isArray(todayAttendance)) {
        return todayAttendance.find(record => record.username === username) || null;
      }
      
      // Fallback to date-based query
      const today = new Date().toISOString().split('T')[0];
      const attendanceData = await attendanceService.getAttendanceByDate(today);
      
      if (Array.isArray(attendanceData)) {
        return attendanceData.find(record => record.username === username) || null;
      }
      
      return null;
    } catch (error) {
      console.error('Error fetching user attendance for today:', error);
      return null;
    }
  }
};

export default attendanceService;

// Named exports for individual functions
export const {
  getAttendanceByDate,
  updateAttendance,
  editAttendanceTimes,
  checkIn,
  checkOut,
  getTodayAttendance,
  getAllSites,
  getUserAttendanceForToday
} = attendanceService;

/**
 * Format attendance time string
 */
export const formatAttendanceTime = (timeString) => {
  if (!timeString) return '--:--';
  
  try {
    // Handle different time formats
    if (timeString.includes('T')) {
      // ISO datetime string
      return new Date(timeString).toLocaleTimeString([], { 
        hour: '2-digit', 
        minute: '2-digit' 
      });
    } else if (timeString.includes(':')) {
      // Time string format (HH:MM:SS or HH:MM)
      const timeParts = timeString.split(':');
      return `${timeParts[0]}:${timeParts[1]}`;
    }
    
    return timeString;
  } catch (error) {
    console.error('Error formatting time:', error);
    return timeString;
  }
};

/**
 * Calculate work duration between check-in and check-out
 */
export const calculateWorkDuration = (checkInTime, checkOutTime) => {
  if (!checkInTime || !checkOutTime) return null;
  
  try {
    const checkIn = new Date(checkInTime);
    const checkOut = new Date(checkOutTime);
    
    const diffMs = checkOut.getTime() - checkIn.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    
    return `${diffHours}h ${diffMinutes}m`;
  } catch (error) {
    console.error('Error calculating work duration:', error);
    return null;
  }
};
